/**
 * Create Team Page - Create teams against projects
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { teamAPI } from '@/api/teamAPI';
import { organizationAPI, Organization } from '@/api/organizationAPI';
import { projectAPI, Project } from '@/api/projectAPI';
import { userAPI, User } from '@/api/userAPI';

interface FormErrors {
  name?: string;
  organization?: string;
  project?: string;
  users?: string;
}

export const CreateTeam = () => {
  const navigate = useNavigate();
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Data loading state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await organizationAPI.getOrganizations({ limit: 100 });
        setOrganizations(orgs);
      } catch (err: any) {
        toast.error('Failed to load organizations');
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, []);
  
  // Fetch projects when organization is selected
  useEffect(() => {
    const fetchProjects = async () => {
      if (!organizationId) {
        setProjects([]);
        setProjectId('');
        return;
      }
      
      try {
        setLoadingProjects(true);
        const projs = await projectAPI.getProjects({ 
          organization_id: organizationId,
          is_active: true,
          limit: 100 
        });
        setProjects(projs);
      } catch (err: any) {
        toast.error('Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [organizationId]);
  
  // Fetch all users from user collection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const usrs = await userAPI.getUsers({ 
          is_active: true,
          limit: 100 
        });
        setUsers(usrs);
      } catch (err: any) {
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);
  
  // Client-side validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (name.trim().length < 3) {
      newErrors.name = 'Team name must be at least 3 characters';
    } else if (name.trim().length > 200) {
      newErrors.name = 'Team name must be less than 200 characters';
    }
    
    if (!organizationId) {
      newErrors.organization = 'Organization is required';
    }
    
    if (!projectId) {
      newErrors.project = 'Project is required';
    }
    
    if (selectedUserIds.length === 0) {
      newErrors.users = 'At least one user must be selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle team creation
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      await teamAPI.createTeam({
        name: name.trim(),
        description: description.trim() || undefined,
        organization_id: organizationId,
        project_id: projectId,
        user_ids: selectedUserIds,
      });
      
      toast.success('Team created successfully!');
      
      // Reset form
      setName('');
      setDescription('');
      setOrganizationId('');
      setProjectId('');
      setSelectedUserIds([]);
      
      // Navigate back to teams or settings
      navigate('/teams');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'Failed to create team. Please try again.';
      toast.error(errorMessage);
      
      // Set specific field errors if available
      if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.toLowerCase().includes('name')) {
          setErrors({ name: detail });
        } else if (detail.toLowerCase().includes('organization')) {
          setErrors({ organization: detail });
        } else if (detail.toLowerCase().includes('project')) {
          setErrors({ project: detail });
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    
    // Clear error when user selects at least one user
    if (errors.users && selectedUserIds.length === 0) {
      setErrors({ ...errors, users: undefined });
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Create Team</h1>
        <p className="text-gray-600 mt-1">Create a new team for a project</p>
      </div>
      
      {/* Form */}
      <div className="card max-w-2xl">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team Details
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Team Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Team Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="Enter team name"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>
          
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full"
              placeholder="Enter team description"
              rows={3}
              disabled={loading}
            />
          </div>
          
          {/* Organization */}
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
              Organization <span className="text-red-500">*</span>
            </label>
            <select
              id="organization"
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setProjectId(''); // Reset project when organization changes
                setSelectedUserIds([]); // Reset users when organization changes
                if (errors.organization) {
                  setErrors({ ...errors, organization: undefined });
                }
              }}
              className={`input w-full ${errors.organization ? 'border-red-500' : ''}`}
              disabled={loading || loadingOrgs}
              required
            >
              <option value="">Select organization...</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            {errors.organization && (
              <p className="mt-1 text-sm text-red-600">{errors.organization}</p>
            )}
          </div>
          
          {/* Project */}
          <div>
            <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-1">
              Project <span className="text-red-500">*</span>
            </label>
            <select
              id="project"
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                if (errors.project) {
                  setErrors({ ...errors, project: undefined });
                }
              }}
              className={`input w-full ${errors.project ? 'border-red-500' : ''}`}
              disabled={loading || loadingProjects || !organizationId}
              required
            >
              <option value="">Select project...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.key})
                </option>
              ))}
            </select>
            {loadingProjects && organizationId && (
              <p className="mt-1 text-xs text-gray-500">Loading projects...</p>
            )}
            {!loadingProjects && organizationId && projects.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">No projects found for this organization</p>
            )}
            {errors.project && (
              <p className="mt-1 text-sm text-red-600">{errors.project}</p>
            )}
          </div>
          
          {/* Users */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team Members <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
              {loadingUsers ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Loading users...</span>
                </div>
              ) : users.length === 0 ? (
                <p className="text-sm text-gray-500">No users found</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        disabled={loading}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {user.display_name || `${user.first_name} ${user.last_name}`}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {errors.users && (
              <p className="mt-1 text-sm text-red-600">{errors.users}</p>
            )}
            {selectedUserIds.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {selectedUserIds.length} user(s) selected
              </p>
            )}
          </div>
          
          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  Create Team
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


